<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">
      <!-- Sticky Filter Header -->
      <div class="table-header-filters">
        <v-row dense class="align-center">
          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="filter-select"
                  :value="formatDateFrom"
                  label="Od datuma"
                  dense
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="reportFromMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="2">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="filter-select"
                  :value="formatDateTo"
                  label="Do datuma"
                  dense
                  outlined
                  hide-details
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="reportToMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="3">
            <v-autocomplete
              class="filter-select"
              dense
              outlined
              hide-details
              label="Prodajno mjesto"
              v-model="location"
              :items="locations"
              item-text="name"
              item-value="id"
              return-object
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="3" lg="3" class="d-flex align-center">
            <v-switch v-model="showUnpaid" label="Uključi neplaćene račune" hide-details class="mt-0 pt-0 custom-switch" color="#3989C9"></v-switch>
          </v-col>

          <!-- Action Cluster -->
          <v-col cols="auto" class="ml-auto d-flex align-center" style="gap: 8px">
            <v-btn :disabled="locationNotSelected" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-menu offset-y left :close-on-content-click="true">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-btn icon class="export-btn" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                      <v-icon color="#00b3be">mdi-export-variant</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense style="border-radius: 12px; overflow: hidden; padding: 4px; min-width: 140px;">
                    <v-list-item @click="changeWidth()">
                      <v-icon size="18" color="#E53935" class="mr-2">mdi-file-pdf-box</v-icon>
                      <v-list-item-title>PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateExcel()">
                      <v-icon size="18" color="#43A047" class="mr-2">mdi-file-excel</v-icon>
                      <v-list-item-title>Excel</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Izvoz podataka</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="to" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Glass Table Wrapper -->
      <div class="glass-table-wrapper">
        <div id="table_info" class="table-content">
          <!-- Hidden company info table for PDF -->
          <table id="firma" style="display: none; font-family: 'Roboto-Regular'">
            <tbody>
              <tr>
                <td>{{ company ? $options.filters.capitalize(company.name) : "" }}</td>
              </tr>
              <tr>
                <td>{{ company ? company.address + ", " + company.city : "" }}</td>
              </tr>
              <tr>
                <td>OIB: {{ company ? company.oib : "" }}</td>
              </tr>
              <tr>
                <td>Prodajno mjesto: {{ location ? location.name : "" }}</td>
              </tr>
              <tr>
                <td>
                  Adresa prodajnog mjesta:
                  {{ location ? location.address + ", " + location.city : "" }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Report Title -->
          <div style="font-family: Raleway, sans-serif;">
            <table justify="center" style="width: 100% !important; border: 0 !important" cellspacing="0" id="title">
              <thead>
                <tr class="pb-0 mb-0" style="border: 0 !important">
                  <th colspan="6" class="pb-0 mb-0">
                    <p class="gradient-text" style="font-size: 25px; text-align: center;">
                      Porezno izvješće
                    </p>
                  </th>
                </tr>
                <tr class="pa-0 ma-0">
                  <th colspan="6">
                    <p style="font-weight: bold; font-size: 12px; text-align: center;">
                      za razdoblje od {{ formatDateFrom }} do {{ formatDateTo }}
                    </p>
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          <!-- Tax Traffic Table -->
          <div style="font-family: Raleway, sans-serif;">
            <table
              justify="center"
              style="width: 100% !important"
              cellspacing="0"
              id="tableTraffic"
              v-if="showTableData"
            >
              <thead>
                <tr :class="['header', 'naslov']" style="text-align: left">
                  <th colspan="5">Pregled poreza po por. grupama</th>
                </tr>
                <tr :class="['header', 'naslov']" style="text-align: left">
                  <th>Iznos</th>
                  <th>Naziv poreza</th>
                  <th>Osnovica</th>
                  <th>Stopa</th>
                  <th>Iznos</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(tax, tidx) in totalByItemPdv" :key="tax.base + tidx">
                  <td>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(tax.total, 1, currency).replace("€", "")
                        : tax.total
                    }}
                  </td>
                  <td>{{ showTaxName(tax.rate) }}</td>
                  <td>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(tax.base, 1, currency).replace("€", "")
                        : tax.base
                    }}
                  </td>
                  <td>{{ tax.rate + ",00 %" }}</td>
                  <td>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(tax.tax, 1, currency).replace("€", "")
                        : tax.tax
                    }}
                  </td>
                </tr>

                <!-- Povratna naknada row -->
                <tr v-if="depositRefund && depositRefund.total > 0">
                  <td>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(depositRefund.total, 100, currency).replace("€", "")
                        : depositRefund.total / 100
                    }}
                  </td>
                  <td style="font-weight: bold">Povratna naknada</td>
                  <td colspan="3" style="text-align: center">-</td>
                </tr>

                <tr class="row-total" style="text-align: left">
                  <th>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(
                            totalPdv.base + totalPdv.tax + (depositRefund && depositRefund.total ? depositRefund.total / 100 : 0),
                            1,
                            currency
                          ).replace("€", "")
                        : totalPdv.base + totalPdv.tax + (depositRefund && depositRefund.total ? depositRefund.total / 100 : 0)
                    }}
                  </th>
                  <th>Ukupno</th>
                  <th>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalPdv.base, 1, currency).replace("€", "")
                        : totalPdv.base
                    }}
                  </th>
                  <th>Ukupno porez</th>
                  <th>
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(
                            totalPdv.tax + (depositRefund && depositRefund.total ? depositRefund.total / 100 : 0),
                            1,
                            currency
                          ).replace("€", "")
                        : totalPdv.tax + (depositRefund && depositRefund.total ? depositRefund.total / 100 : 0)
                    }}
                  </th>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Payment Methods Table -->
          <div style="font-family: Raleway, sans-serif;">
            <table
              justify="center"
              style="width: 100% !important"
              class="pt-5"
              cellspacing="0"
              v-if="showTableData"
              id="tablePayments"
            >
              <thead>
                <tr :class="['header', 'naslov']" style="text-align: left">
                  <th>Način plaćanja</th>
                  <th>Total</th>
                  <th>Naziv poreza</th>
                  <th>Osnovica</th>
                  <th>Stopa</th>
                  <th>Iznos</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, cidx) in cash.pdv" :key="c.base + cidx">
                  <td v-if="cidx === 0" :rowspan="cash.pdv.length + 1" style="font-weight: bold">
                    Gotovina
                  </td>
                  <td
                    v-if="cidx === 0"
                    :rowspan="cash.pdv.length + 1"
                    style="text-align: right; font-weight: bold"
                  >
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(cash.total, 1, currency).replace("€", "")
                        : cash.total
                    }}
                  </td>
                  <td style="text-align: right">{{ showTaxName(c.rate) }}</td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.base, 1, currency).replace("€", "")
                        : c.base
                    }}
                  </td>
                  <td style="text-align: right">{{ c.rate + ",00 %" }}</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.tax, 1, currency).replace("€", "")
                        : c.tax
                    }}
                  </td>
                </tr>
                <tr v-if="cash && cash.pdv && cash.pdv.length > 0 && cash.depositRefund > 0">
                  <td style="text-align: right; font-weight: bold">Povratna naknada</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(cash.depositRefund || 0, 1, currency).replace("€", "")
                        : cash.depositRefund || 0
                    }}
                  </td>
                  <td style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalCashPdv.tax, 1, currency).replace("€", "")
                        : totalCashPdv.tax
                    }}
                  </td>
                </tr>
                <tr v-else-if="cash && cash.pdv && cash.pdv.length > 0">
                  <td colspan="3" style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalCashPdv.tax, 1, currency).replace("€", "")
                        : totalCashPdv.tax
                    }}
                  </td>
                </tr>
                <tr v-for="(c, cidx) in card.pdv" :key="c.base + cidx">
                  <td v-if="cidx === 0" :rowspan="card.pdv.length + 1" style="font-weight: bold">
                    Kartice
                  </td>
                  <td
                    v-if="cidx === 0"
                    :rowspan="card.pdv.length + 1"
                    style="text-align: right; font-weight: bold"
                  >
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(card.total, 1, currency).replace("€", "")
                        : card.total
                    }}
                  </td>
                  <td style="text-align: right">{{ showTaxName(c.rate) }}</td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.base, 1, currency).replace("€", "")
                        : c.base
                    }}
                  </td>
                  <td style="text-align: right">{{ c.rate + ",00 %" }}</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.tax, 1, currency).replace("€", "")
                        : c.tax
                    }}
                  </td>
                </tr>
                <tr v-if="card && card.pdv && card.pdv.length > 0 && card.depositRefund > 0">
                  <td style="text-align: right; font-weight: bold">Povratna naknada</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(card.depositRefund || 0, 1, currency).replace("€", "")
                        : card.depositRefund || 0
                    }}
                  </td>
                  <td style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalCardPdv.tax, 1, currency).replace("€", "")
                        : totalCardPdv.tax
                    }}
                  </td>
                </tr>
                <tr v-else-if="card && card.pdv && card.pdv.length > 0">
                  <td colspan="3" style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalCardPdv.tax, 1, currency).replace("€", "")
                        : totalCardPdv.tax
                    }}
                  </td>
                </tr>

                <tr v-for="(c, cidx) in other.pdv" :key="c.base + cidx">
                  <td v-if="cidx === 0" :rowspan="other.pdv.length + 1" style="font-weight: bold">
                    Ostalo
                  </td>
                  <td
                    v-if="cidx === 0"
                    :rowspan="other.pdv.length + 1"
                    style="text-align: right; font-weight: bold"
                  >
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(other.total, 1, currency).replace("€", "")
                        : other.total
                    }}
                  </td>
                  <td style="text-align: right">{{ showTaxName(c.rate) }}</td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.base, 1, currency).replace("€", "")
                        : c.base
                    }}
                  </td>
                  <td style="text-align: right">{{ c.rate + ",00 %" }}</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.tax, 1, currency).replace("€", "")
                        : c.tax
                    }}
                  </td>
                </tr>
                <tr v-if="other && other.pdv && other.pdv.length > 0 && other.depositRefund > 0">
                  <td style="text-align: right; font-weight: bold">Povratna naknada</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(other.depositRefund || 0, 1, currency).replace("€", "")
                        : other.depositRefund || 0
                    }}
                  </td>
                  <td style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalOtherPdv.tax, 1, currency).replace("€", "")
                        : totalOtherPdv.tax
                    }}
                  </td>
                </tr>
                <tr v-else-if="other && other.pdv && other.pdv.length > 0">
                  <td colspan="3" style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalOtherPdv.tax, 1, currency).replace("€", "")
                        : totalOtherPdv.tax
                    }}
                  </td>
                </tr>

                <tr v-for="(c, cidx) in transactional.pdv" :key="c.base + cidx">
                  <td v-if="cidx === 0" :rowspan="transactional.pdv.length + 1" style="font-weight: bold">
                    Transakcijski račun
                  </td>
                  <td
                    v-if="cidx === 0"
                    :rowspan="transactional.pdv.length + 1"
                    style="text-align: right; font-weight: bold"
                  >
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(transactional.total, 1, currency).replace("€", "")
                        : transactional.total
                    }}
                  </td>
                  <td style="text-align: right">{{ showTaxName(c.rate) }}</td>
                  <td style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.base, 1, currency).replace("€", "")
                        : c.base
                    }}
                  </td>
                  <td style="text-align: right">{{ c.rate + ",00 %" }}</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(c.tax, 1, currency).replace("€", "")
                        : c.tax
                    }}
                  </td>
                </tr>
                <tr v-if="transactional && transactional.pdv && transactional.pdv.length > 0 && transactional.depositRefund > 0">
                  <td style="text-align: right; font-weight: bold">Povratna naknada</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(transactional.depositRefund || 0, 1, currency).replace("€", "")
                        : transactional.depositRefund || 0
                    }}
                  </td>
                  <td style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalTransactionalPdv.tax, 1, currency).replace("€", "")
                        : totalTransactionalPdv.tax
                    }}
                  </td>
                </tr>
                <tr v-else-if="transactional && transactional.pdv && transactional.pdv.length > 0">
                  <td colspan="3" style="text-align: right; font-weight: bold">Ukupno</td>
                  <td style="text-align: right; font-weight: bold">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalTransactionalPdv.tax, 1, currency).replace("€", "")
                        : totalTransactionalPdv.tax
                    }}
                  </td>
                </tr>

                <!-- Final total row -->
                <tr class="row-total" style="text-align: left">
                  <th style="text-align: right">Ukupno total</th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(
                            totalPdv.base + totalPdv.tax + (depositRefund && depositRefund.total ? depositRefund.total / 100 : 0),
                            1,
                            currency
                          ).replace("€", "")
                        : totalPdv.base + totalPdv.tax + (depositRefund && depositRefund.total ? depositRefund.total / 100 : 0)
                    }}
                  </th>
                  <th colspan="3" style="text-align: right">Ukupno porez</th>
                  <th style="text-align: right">
                    {{
                      viewType === "pdf"
                        ? $options.filters.money(totalPdv.tax, 1, currency).replace("€", "")
                        : totalPdv.tax
                    }}
                  </th>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { DAY, clone } from '@/plugins/utils'
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import 'jspdf-autotable'
import { saveExcel } from '@/modules/all-reports/xlsx/config.js'
import EventBus from '@/plugins/event-bus'
import { initPdf, setTable, setPageNumber } from '@/modules/all-reports/pdf/config.js'
import avansDiscount from '@/mixins/avans'

export default {
  name: 'Reports',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  components: {},
  data: function () {
    return {
      dateFrom: new Date(new Date().getTime() - DAY).toISOString().substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),
      locations: [],
      searchLocation: null,
      allReceipts: [],
      reportFromMenu: undefined,
      reportToMenu: undefined,
      company: undefined,
      location: undefined,
      payments: {
        pdv: [],
        pdvTotal: { base: 0, rate: 'Ukupno', total: 0 }
      },
      tableWidth: '1200px',
      a4PortraitWidth: '575px',
      a4LandscapeWidth: '610px',
      targetModifier: '',
      viewType: 'pdf',
      showTableData: false,
      multiplier: 0,
      listeners: [],
      totalByItemPdv: [],
      totalByReceiptPdv: [],
      totalPdv: {},
      currency: 'EUR',
      cash: { pdv: [{ rate: 0, total: 0, tax: 0 }], total: 0, depositRefund: 0 },
      card: { pdv: [{ rate: 0, total: 0, tax: 0 }], total: 0, depositRefund: 0 },
      other: { pdv: [{ rate: 0, total: 0, tax: 0 }], total: 0, depositRefund: 0 },
      transactional: { pdv: [{ rate: 0, total: 0, tax: 0 }], total: 0, depositRefund: 0 },
      totalCashPdv: {},
      totalCardPdv: {},
      totalOtherPdv: {},
      totalTransactionalPdv: {},
      depositRefund: { total: 0 },
      totalDepositRefund: 0,
      showUnpaid: false,
      margin: '0px'
    }
  },
  computed: {
    to () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    formatDateTo () {
      return new Intl.DateTimeFormat('hr-HR', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).format(new Date(this.dateTo))
    },
    formatDateFrom () {
      return new Intl.DateTimeFormat('hr-HR', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).format(new Date(this.dateFrom))
    },
    locationNotSelected () {
      return !this.location || !this.location.id
    }
  },
  async created () {
    this.margin = state.getMargin()
    await this.getPointsOfSale()
  },
  mounted () {
    this.getMultiplier()

    var sideNav = this.$route.meta.sideNav
    switch (sideNav) {
      case 'Companies':
        break
      case 'Company':
        this.targetModifier = 'company'
        break
      case 'PointOfSale':
        this.targetModifier = 'pointOfSale'
        break
      case 'CashRegister':
        this.targetModifier = 'pointOfSale'
        break
    }

    EventBus.$on('set-mini', () => {
      this.margin = state.getMargin()
    })

    this.company = clone(state.getCurrentCompany())
    this.location = clone(state.getPointOfSale())

    if (!this.location || !this.location.id) {
      this.location = this.locations.find((item) => item.id === 'all')
    }

    if (this.company.type === 'pausalni_obrt') {
      this.showUnpaid = false
    } else {
      this.showUnpaid = true
    }
  },
  methods: {
    showTaxName (rate) {
      var tax = 'PDV '

      return tax + rate + ' %'
    },
    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot((doc) => {
        this.config = doc.data()
        this.multiplier = doc.data().item_amount.decimal_multiplier
      })
      this.listeners.push(listener)
    },
    getPdvDiscounted (receipt, reprezentacija) {
      var tempPdv = []
      if (receipt.pdv_discounted && receipt.pdv_discounted.length > 0) {
        if (reprezentacija === true) {
          var b = 160
          var t = 200
          var tx = 40
          var rate = 2500

          if (receipt.issue_date_time > 1672531210) {
            b = 40
            t = 50
            tx = 10
          }

          if (receipt.type.flag === 'STORNO') {
            b *= -1
            t *= -1
            tx *= -1
          }

          if (state.getCurrentCompany().is_taxed === false) {
            rate = 0
            b = 50
            t = 50
            tx = 0
          }

          tempPdv.push({
            rate: rate,
            base: b,
            total: t,
            tax: tx,
            count: 1,
            paymentMethod: receipt.payment_method
          })
        } else {
          receipt.pdv_discounted.forEach((pdv) => {
            tempPdv.push({
              rate: pdv.rate,
              base: pdv.base,
              total: pdv.total + pdv.base,
              tax: pdv.total,
              count: 1,
              paymentMethod: receipt.payment_method
            })
          })
        }
      }

      return tempPdv
    },
    getPdvAndRemoveDiscountIfExist (receipt, reprezentacija) {
      var tempPdv = []
      if (receipt.pdv && receipt.pdv.length > 0) {
        if (reprezentacija === true) {
          var b = 160
          var t = 200
          var tx = 40

          if (receipt.issue_date_time > 1672531210) {
            b = 40
            t = 50
            tx = 10
          }

          if (receipt.type.flag === 'STORNO') {
            b *= -1
            t *= -1
            tx *= -1
          }

          tempPdv.push({
            rate: 2500,
            base: b,
            total: t,
            tax: tx,
            count: 1,
            paymentMethod: receipt.payment_method
          })
        } else {
          receipt.pdv.forEach((pdv) => {
            tempPdv.push({
              rate: pdv.rate,
              base: pdv.base,
              total: pdv.total + pdv.base,
              tax: pdv.total,
              count: 1,
              paymentMethod: receipt.payment_method
            })
          })
        }
      }

      if (tempPdv && tempPdv.length > 0) {
        // Ako postoji popust na iznos racuna onda skini na pojedinacnim artiklima
        var d = clone(receipt.discount)
        if (d && d.active === true && d.type !== '' && reprezentacija === false) {
          let totalRemove = d.amount / 100

          tempPdv.sort((a, b) => b.rate - a.rate) // b - a for reverse sort

          if (d.name === 'AVANS-STORNO-RECEIPT') {
            if (receipt.type.flag !== 'STORNO') {
              tempPdv = avansDiscount(tempPdv, receipt.tags)
            }
          } else {
            tempPdv.forEach((p, pkey) => {
              if (p.base > 0 && p.total > 0) {
                let totalPrice = p.total / 100

                if (totalPrice >= 1 + p.rate / 10000) {
                  let removedAllPossible = false
                  let remove = totalRemove

                  // kreni unazad, uzmi ukupni iznos za skidanje i probaj skinuti
                  for (var j = totalRemove; j > 0 && removedAllPossible === false; j--) {
                    // ako oduzmes iznos i on je veci od 100 na kraju i ustvari si skinuo sve, tu zavrsi
                    if (totalPrice - j >= 1 + p.rate / 10000) {
                      removedAllPossible = true
                      remove = j
                    }
                  }

                  if (removedAllPossible === true) {
                    totalPrice -= remove
                    totalRemove -= remove
                  }
                }

                tempPdv[pkey].total = totalPrice * 100
                tempPdv[pkey].base = parseInt(parseFloat(tempPdv[pkey].total) / (1 + parseFloat(tempPdv[pkey].rate) / 10000))
                tempPdv[pkey].tax = tempPdv[pkey].total - tempPdv[pkey].base
              }
            })
          }
        }
      }

      return tempPdv
    },
    async getData () {
      this.showLoader()
      const dateFrom = new Date(this.dateFrom).getTime() / 1000
      const dateTo = new Date(this.dateTo).getTime() / 1000
      var cash = {
        pdv: [],
        total: 0,
        depositRefund: 0
      }

      var card = {
        pdv: [],
        total: 0,
        depositRefund: 0
      }

      var other = {
        pdv: [],
        total: 0,
        depositRefund: 0
      }

      var transactional = {
        pdv: [],
        total: 0,
        depositRefund: 0
      }

      var depositRefund = {
        total: 0
      }

      this.allReceipts = []

      this.totalCashPdv = { base: 0, tax: 0, total: 0 }
      this.totalCardPdv = { base: 0, tax: 0, total: 0 }
      this.totalOtherPdv = { base: 0, tax: 0, total: 0 }
      this.totalTransactionalPdv = { base: 0, tax: 0, total: 0 }
      this.totalDepositRefund = 0
      this.totalPdv = { base: 0, tax: 0, total: 0 }

      let query = df
        .collection(`receipts/${state.getCurrentCompany().id}/receipts`)
        .where('issue_date_time', '>=', dateFrom)
        .where('issue_date_time', '<=', dateTo + 86399)

      if (this.location && this.location.id !== 'all') {
        query = query.where('location_details.id', '==', this.location.id)
      }

      await query.get().then((documentSnapshots) => {
        if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
          documentSnapshots.docs.forEach((item) => {
            var rcpt = item.data()
            if (this.showUnpaid === false) {
              if (rcpt.payment && (rcpt.payment.current_status === 'PAID' || rcpt.payment.current_status === 'REVERSED')) {
                this.allReceipts.push(item.data())
              }
            } else {
              this.allReceipts.push(item.data())
            }
          })
        }
      })

      this.allReceipts.sort((a, b) => a.issue_date_time - b.issue_date_time)

      var totalByReceiptPdv = []
      this.allReceipts.forEach((receipt) => {
        var reprezentacija = false
        if (receipt.tags) {
          receipt.tags.forEach((tag) => {
            if (tag.receipt_type && tag.receipt_type === 'REPREZENTACIJA') {
              reprezentacija = true
            }
          })
        }

        var tempPdv = []
        if (receipt.pdv_discounted && receipt.pdv_discounted.length > 0) {
          tempPdv = this.getPdvDiscounted(receipt, reprezentacija)
        } else if (receipt.pdv && receipt.pdv.length > 0) {
          tempPdv = this.getPdvAndRemoveDiscountIfExist(receipt, reprezentacija)
        }

        if (tempPdv && tempPdv.length > 0) {
          tempPdv.forEach((pdv) => {
            const base = Math.round(pdv.base * 100) / 100
            const total = Math.round(pdv.total * 100) / 100
            const tax = Math.round(pdv.tax * 100) / 100

            const idx = totalByReceiptPdv.findIndex((p) => p?.rate === pdv.rate)
            if (idx > -1) {
              totalByReceiptPdv[idx].base += base
              totalByReceiptPdv[idx].total += total
              totalByReceiptPdv[idx].tax += tax
              totalByReceiptPdv[idx].count += 1
              // Dodaj depositRefund polje ako ne postoji
              if (!totalByReceiptPdv[idx].depositRefund) {
                totalByReceiptPdv[idx].depositRefund = 0
              }
            } else {
              totalByReceiptPdv.push({
                rate: pdv.rate,
                base: base,
                total: total,
                tax: tax,
                count: 1,
                depositRefund: 0
              })
            }

            // Podaci za drugu tablicu po načinima plaćanja
            if (pdv.paymentMethod === 'NOVČANICE') {
              this.totalCashPdv.base += base / 100
              this.totalCashPdv.tax += tax / 100
              this.totalCashPdv.total += total / 100

              const idx = cash.pdv.findIndex((p) => p?.rate === pdv.rate / 100)
              if (idx > -1) {
                cash.pdv[idx].base += base / 100
                cash.pdv[idx].tax += tax / 100
                cash.total += total / 100
              } else {
                cash.total += total / 100
                var p = {
                  base: base / 100,
                  rate: pdv.rate / 100,
                  tax: tax / 100
                }
                cash.pdv.push(p)
              }
            }

            if (pdv.paymentMethod === 'KARTICA') {
              this.totalCardPdv.base += base / 100
              this.totalCardPdv.tax += tax / 100
              this.totalCardPdv.total += total / 100

              const idx = card.pdv.findIndex((p) => p?.rate === pdv.rate / 100)
              if (idx > -1) {
                card.pdv[idx].base += base / 100
                card.pdv[idx].tax += tax / 100
                card.total += total / 100
              } else {
                card.total += total / 100
                p = {
                  base: base / 100,
                  rate: pdv.rate / 100,
                  tax: tax / 100
                }
                card.pdv.push(p)
              }
            }

            if (pdv.paymentMethod === 'TRANSAKCIJSKI RAČUN') {
              this.totalTransactionalPdv.base += base / 100
              this.totalTransactionalPdv.tax += tax / 100
              this.totalTransactionalPdv.total += total / 100

              const idx = transactional.pdv.findIndex((p) => p?.rate === pdv.rate / 100)
              if (idx > -1) {
                transactional.pdv[idx].base += base / 100
                transactional.pdv[idx].tax += tax / 100
                transactional.total += total / 100
              } else {
                transactional.total += total / 100
                p = {
                  base: base / 100,
                  rate: pdv.rate / 100,
                  tax: tax / 100
                }
                transactional.pdv.push(p)
              }
            }

            if (pdv.paymentMethod === 'OSTALO') {
              this.totalOtherPdv.base += base / 100
              this.totalOtherPdv.tax += tax / 100
              this.totalOtherPdv.total += total / 100

              const idx = other.pdv.findIndex((p) => p?.rate === pdv.rate / 100)
              if (idx > -1) {
                other.pdv[idx].base += base / 100
                other.pdv[idx].tax += tax / 100
                other.total += total / 100
              } else {
                other.total += total / 100
                p = {
                  base: base / 100,
                  rate: pdv.rate / 100,
                  tax: tax / 100
                }
                other.pdv.push(p)
              }
            }
          })
        }

        // Zbroji povratnu naknadu iz ovog računa i dodaj ju u PDV grupe
        if (receipt.naknade && receipt.naknade.povratna && receipt.naknade.povratna.total_amount) {
          depositRefund.total += receipt.naknade.povratna.total_amount

          // Dodaj povratnu naknadu u totalByReceiptPdv po grupama
          // Povratna naknada je uvijek 10 centi po artiklu, raspodijeli proporcionalno
          if (tempPdv && tempPdv.length > 0) {
            const depositRefundForReceipt = receipt.naknade.povratna.total_amount / 100 // u EUR
            const totalAllPdv = tempPdv.reduce((sum, p) => sum + Math.round(p.total * 100) / 100, 0)

            tempPdv.forEach((pdv) => {
              const total = Math.round(pdv.total * 100) / 100
              const proportion = total / totalAllPdv
              const depositRefundForThisPdv = depositRefundForReceipt * proportion

              const idx = totalByReceiptPdv.findIndex((p) => p?.rate === pdv.rate)
              if (idx > -1) {
                totalByReceiptPdv[idx].depositRefund += depositRefundForThisPdv
              }
            })
          }

          // Dodaj povratnu naknadu u način plaćanja ovisno o payment_method
          const depositRefundEur = receipt.naknade.povratna.total_amount / 100
          if (receipt.payment_method === 'NOVČANICE') {
            cash.total += depositRefundEur
            cash.depositRefund += depositRefundEur
            this.totalCashPdv.total += depositRefundEur
          } else if (receipt.payment_method === 'KARTICA') {
            card.total += depositRefundEur
            card.depositRefund += depositRefundEur
            this.totalCardPdv.total += depositRefundEur
          } else if (receipt.payment_method === 'TRANSAKCIJSKI RAČUN') {
            transactional.total += depositRefundEur
            transactional.depositRefund += depositRefundEur
            this.totalTransactionalPdv.total += depositRefundEur
          } else if (receipt.payment_method === 'OSTALO') {
            other.total += depositRefundEur
            other.depositRefund += depositRefundEur
            this.totalOtherPdv.total += depositRefundEur
          }
        }

        // Dodaj PNP u način plaćanja (pdv_discounted ne sadrži PNP iznos)
        if (receipt.pnp && receipt.pnp.total) {
          const pnpEur = receipt.pnp.total / 100
          if (receipt.payment_method === 'NOVČANICE') {
            cash.total += pnpEur
            this.totalCashPdv.total += pnpEur
          } else if (receipt.payment_method === 'KARTICA') {
            card.total += pnpEur
            this.totalCardPdv.total += pnpEur
          } else if (receipt.payment_method === 'TRANSAKCIJSKI RAČUN') {
            transactional.total += pnpEur
            this.totalTransactionalPdv.total += pnpEur
          } else if (receipt.payment_method === 'OSTALO') {
            other.total += pnpEur
            this.totalOtherPdv.total += pnpEur
          }
        }
      })

      // Sluzi samo za formatiranje i zbrajanje ukupnog stanja
      this.totalByItemPdv = totalByReceiptPdv

      this.totalPdv.base = 0
      this.totalPdv.tax = 0
      this.totalPdv.total = 0
      this.totalPdv.depositRefund = 0
      this.totalPdv.totalDifference = 0

      this.totalByItemPdv.forEach((pdv, pIdx) => {
        this.totalByItemPdv[pIdx].base /= 100
        this.totalByItemPdv[pIdx].rate /= 100
        this.totalByItemPdv[pIdx].tax /= 100
        this.totalByItemPdv[pIdx].total /= 100
        // depositRefund je već u EUR, ne treba dijeliti sa 100

        this.totalByItemPdv[pIdx].base = Math.round(this.totalByItemPdv[pIdx].base * 100) / 100
        this.totalByItemPdv[pIdx].rate = Math.round(this.totalByItemPdv[pIdx].rate * 100) / 100
        this.totalByItemPdv[pIdx].tax = Math.round(this.totalByItemPdv[pIdx].tax * 100) / 100
        this.totalByItemPdv[pIdx].total = Math.round(this.totalByItemPdv[pIdx].total * 100) / 100
        this.totalByItemPdv[pIdx].depositRefund = Math.round((this.totalByItemPdv[pIdx].depositRefund || 0) * 100) / 100

        this.totalPdv.base += pdv.base
        this.totalPdv.tax += pdv.tax
        this.totalPdv.total += pdv.total
        this.totalPdv.depositRefund += pdv.depositRefund || 0

        // Izračunaj razliku: Iznos - (Osnovica + PDV)
        const difference = pdv.total - (pdv.base + pdv.tax)
        this.totalByItemPdv[pIdx].difference = Math.round(difference * 100) / 100
        this.totalPdv.totalDifference += difference
      })

      this.totalPdv.depositRefund = Math.round(this.totalPdv.depositRefund * 100) / 100
      this.totalPdv.totalDifference = Math.round(this.totalPdv.totalDifference * 100) / 100

      this.cash = cash
      this.card = card
      this.other = other
      this.transactional = transactional
      this.depositRefund = depositRefund
      this.showTableData = true

      this.hideLoader()
    },

    changeWidth () {
      this.showLoader('', { opacity: 1 })
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = this.a4PortraitWidth
      tableInfo.style.fontSize = '80%'

      this.generatePdf()
    },
    async generatePdf () {
      const that = this
      const tableInfo = document.getElementById('table_info')
      try {
        var doc = await initPdf()

        doc = await setTable(doc, '#firma', 10)
        var finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#title', finalY)
        finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#tableTraffic', finalY)
        finalY = doc.lastAutoTable.finalY || 10
        doc = await setTable(doc, '#tablePayments', finalY + 10)

        doc = await setPageNumber(doc)

        doc.save('Porezno izvješće.pdf')
      } catch (error) {
        console.error('PDF generation error:', error)
      } finally {
        tableInfo.style.width = that.tableWidth
        tableInfo.style.fontSize = '100%'
        this.viewType = 'pdf'
        that.hideLoader()
      }
    },
    generateExcel () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        var tables = ['firma', 'title', 'tableTraffic']
        var fileName = 'Porezno izvješće.xlsx'

        saveExcel(tables, fileName)

        this.viewType = 'pdf'
        this.hideLoader()
      }, 1000)
    },
    async getPointsOfSale () {
      const docRef = df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
      docRef.onSnapshot((doc) => {
        var locations = Object.values(doc.data().locations)
        locations.forEach((location, index) => {
          this.locations.push(location)
        })
        // Sort alphabetically
        this.locations.sort((a, b) => a.name.localeCompare(b.name))
      })
    }
  }
}
</script>

<style scoped>
/* Report Wrapper */
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

/* Sticky Filter Header */
.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

.table-header-filters >>> .v-row {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.table-header-filters >>> .v-row:first-child {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Switch Wrapper */
.switch-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  padding-top: 4px;
}

.custom-switch >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.custom-switch >>> .v-label {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
}

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}

.search-btn:disabled {
  opacity: 0.45 !important;
}

.export-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(0, 179, 190, 0.06) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  transition: all 0.2s ease !important;
}

.export-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: 0 0 0 3px rgba(0, 179, 190, 0.12) !important;
}

/* Glass Table Wrapper */
.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 30px;
  overflow-x: auto;
}

/* Table Content */
.table-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  font-size: 16px;
  font-family: 'Roboto-Regular', sans-serif;
}

/* Gradient Text */
.gradient-text {
  font-weight: 700 !important;
  color: #2d3748 !important;
  -webkit-text-fill-color: #2d3748 !important;
  letter-spacing: 0.3px !important;
  display: inline-block !important;
  margin: 0;
  padding: 0;
}

/* Table Styling */
#tableTraffic {
  border-collapse: collapse;
}

#tablePayments {
  border-collapse: collapse;
  margin-top: 20px;
}

.glass-table-wrapper table {
  border-collapse: collapse;
  width: 100%;
  border: none;
}

.glass-table-wrapper table th,
.glass-table-wrapper table td {
  padding: 14px 20px;
  border: none;
  border-bottom: 1px solid #EDF2F7;
  font-variant-numeric: tabular-nums;
}

.glass-table-wrapper table tbody tr:hover:not(.naslov):not(.row-total) {
  background: #F7FAFC;
}

/* Header Row */
.naslov {
  background: #F8FAFC !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  color: #2D3748 !important;
}
.naslov th, .naslov td {
  color: #2D3748 !important;
  border-bottom: 2px solid #EDF2F7 !important;
}

.row-total {
  background: rgba(0, 188, 212, 0.03) !important;
  border-top: 2px solid rgba(0, 188, 212, 0.2) !important;
}
.row-total th, .row-total td {
  color: #00b3be !important;
  font-weight: 700 !important;
  border-bottom: 1px solid rgba(0, 188, 212, 0.15) !important;
}

.report-content-wrapper {
  margin-top: 40px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .glass-table-wrapper {
    padding: 16px;
  }

  .table-content {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .table-header-filters {
    padding: 12px;
  }

  .glass-table-wrapper {
    padding: 12px;
  }

  .table-content {
    font-size: 13px;
  }
}
</style>
